import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { cn } from 'src/utils/cn'
import useClickOutside from 'src/common/hooks/useClickOutside'

const TRIGGER_RESET = cn(
  'm-0 cursor-pointer appearance-none border-0 bg-transparent p-0',
  '[text-align:inherit] tracking-[inherit] text-inherit [text-transform:inherit] [font:inherit]'
)

const Tooltip = ({
  id,
  body,
  children,
  triggerClassName,
  bodyClassName,
  arrowClassName,
  containerClassName,
  closeDelay = 150,
  placement = 'bottom',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef(null)
  const closeTimerRef = useRef(null)
  const pointerTypeRef = useRef(null)

  const containerRef = useClickOutside(() => setIsOpen(false))

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const scheduleClose = useCallback(() => {
    cancelClose()
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false)
      closeTimerRef.current = null
    }, closeDelay)
  }, [cancelClose, closeDelay])

  useEffect(() => () => cancelClose(), [cancelClose])

  useEffect(() => {
    if (!isOpen) return undefined
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
        buttonRef.current?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  useEffect(() => {
    const handleTouchEnd = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('touchend', handleTouchEnd)

    return () => document.removeEventListener('touchend', handleTouchEnd)
  }, [containerRef])

  const handlePointerEnter = (e) => {
    if (e.pointerType === 'mouse') {
      cancelClose()
      setIsOpen(true)
    }
  }

  const handlePointerLeave = (e) => {
    if (e.pointerType === 'mouse') {
      scheduleClose()
    }
  }

  const handlePointerDown = (e) => {
    pointerTypeRef.current = e.pointerType
  }

  const handleClick = () => {
    if (pointerTypeRef.current === 'touch') {
      setIsOpen((prev) => !prev)
    }
  }

  const handleFocus = (e) => {
    cancelClose()
    if (e.target.matches?.(':focus-visible')) {
      setIsOpen(true)
    }
  }

  const handleBlur = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return
    scheduleClose()
  }

  return (
    <span
      ref={containerRef}
      className={cn('relative inline-block', containerClassName)}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-describedby={id}
        aria-expanded={isOpen}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
        className={cn(TRIGGER_RESET, triggerClassName)}
      >
        {children}
      </button>
      <span
        id={id}
        role="tooltip"
        aria-hidden={!isOpen}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        className={cn(
          'absolute left-1/2 z-50 -translate-x-1/2',
          placement === 'top'
            ? 'bottom-[calc(100%+8px)]'
            : 'top-[calc(100%+8px)]',
          'max-w-55 w-max rounded-lg p-2 sm:max-w-60',
          'bg-blue-950 text-left text-14 font-normal normal-case leading-[140%] tracking-normal text-white',
          'transition-opacity duration-150',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
          bodyClassName
        )}
      >
        {body}
        <span
          aria-hidden
          className={cn(
            'size-3 absolute left-1/2 -translate-x-1/2 rotate-45 bg-blue-950',
            placement === 'top' ? 'bottom-[-6px]' : 'top-[-6px]',
            arrowClassName
          )}
        />
      </span>
    </span>
  )
}

Tooltip.propTypes = {
  id: PropTypes.string.isRequired,
  body: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  triggerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  arrowClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  closeDelay: PropTypes.number,
  placement: PropTypes.oneOf(['top', 'bottom']),
}

export default Tooltip
